import isEmpty from 'lodash/isEmpty';

export const PRESIGNED_URL_AUTH_HEADER = 'x-up2rent-authorization';

/** The `bucket` values /api/presigned-url accepts: the client files bucket or the admintool invoices bucket. */
export type PresignedUrlBucket = 'files' | 'adminInvoices';
export const PUBLIC_FILE_PREFIX = 'public/';

// Folder stays first (`reportTemp/<clientId>/...`) to keep matching the bucket lifecycle prefixes.
export const TEMP_FILE_KEY_PREFIXES = ['reportTemp/', 'accountingExportTemp/', 'convertorTemp/'];

export const getPublicFileKey = (fileKey: string): string =>
  fileKey.startsWith(PUBLIC_FILE_PREFIX) ? fileKey : `${PUBLIC_FILE_PREFIX}${fileKey}`;

const DECRYPTED_COPY_SUFFIX = '.decrypted.pdf';

/** Decrypted working copy of an encrypted PDF, next to its original (REN-5018): deleted with it. */
export const getDecryptedCopyKey = (fileKey: string): string => `${fileKey}${DECRYPTED_COPY_SUFFIX}`;

export const getClientScopedFileKey = (fileKey: string): string =>
  getPublicFileKey(fileKey).slice(PUBLIC_FILE_PREFIX.length);

export const getTempFileKeyClientId = (fileKey: string): string | undefined => {
  const scopedKey = getClientScopedFileKey(fileKey);
  const tempPrefix = TEMP_FILE_KEY_PREFIXES.find((prefix) => scopedKey.startsWith(prefix));
  if (!tempPrefix) {
    return undefined;
  }
  const [clientId] = scopedKey.slice(tempPrefix.length).split('/');
  return isEmpty(clientId) ? undefined : clientId;
};

export const isFileKeyOwnedByClient = (fileKey: string, clientId: string): boolean => {
  if (isEmpty(clientId)) {
    return false;
  }
  const scopedKey = getClientScopedFileKey(fileKey);
  const tempPrefix = TEMP_FILE_KEY_PREFIXES.find((prefix) => scopedKey.startsWith(prefix));
  if (tempPrefix) {
    return scopedKey.slice(tempPrefix.length).startsWith(`${clientId}/`);
  }
  return scopedKey.startsWith(`${clientId}/`);
};

/** Every public prefix holding the client's files: its own folder and its folder under each temp prefix. */
export const getClientFilePrefixes = (clientId: string): string[] =>
  [`${clientId}/`, ...TEMP_FILE_KEY_PREFIXES.map((tempPrefix) => `${tempPrefix}${clientId}/`)].map((prefix) =>
    getPublicFileKey(prefix)
  );
